import React from 'react';
import { useOutletContext } from 'react-router-dom';
import AssetCard from '../AssetCard';
import Diagnostics from '../Diagnostics';
import Instructions from '../Instructions';
import TicketDocs from '../docs/TicketDocs';
import TicketTodos from '../todos/TicketTodos';
import TicketAttachments from '../TicketAttachments';
import ActivityTrail from '../ActivityTrail';
import { useAssetMeta } from '../../lib/assets';

/**
 * The quieter tabs. Each is the panel that already did the job, moved here
 * unchanged and loaded only when its tab is opened - which is the twelve
 * requests per ticket the old page made whether anybody looked or not.
 */

/** DEVICE: the machine, its diagnostics and the instructions sent to it. */
export function DeviceTab() {
  const { ticket, role, reload, contractorMode, auth, locked } = useOutletContext();
  const assetMeta = useAssetMeta(role === 'admin');
  if (!ticket.asset) {
    const mayAdd = role === 'admin' && assetMeta?.enabled === true && 'asset_id' in ticket;
    return (
      <div className="tk-device">
        {mayAdd ? (
          <AssetCard asset={null} ticketId={ticket.id} requesterId={ticket.requester_id}
                     onChanged={reload} canAttach />
        ) : <p className="tk-empty">No device on this ticket.</p>}
      </div>
    );
  }
  return (
    <div className="tk-device">
      {contractorMode ? (
        <AssetCard asset={ticket.asset} contractor ticketId={ticket.id}
                   canRecordRepair={!locked} authHeaders={auth?.headers || null} />
      ) : (
        <AssetCard asset={ticket.asset} ticketId={ticket.id} requesterId={ticket.requester_id}
                   onChanged={reload} canAttach={role === 'admin' && assetMeta?.enabled === true} />
      )}
      <Diagnostics path={`/tickets/${ticket.id}/diagnostics`} authHeaders={auth?.headers || null}
                   title={contractorMode ? 'Diagnostics from the device' : 'Diagnostics for this ticket'} />
      <Instructions ticketId={ticket.id} authHeaders={auth?.headers || null} />
    </div>
  );
}

/** DOCS & TO-DOS: the articles linked to it, and the small jobs it needs. */
export function DocsTab() {
  const { ticket, todosOn, contractorMode, auth, contractor } = useOutletContext();
  // A server without to-do lists (503) answers once and the panel goes;
  // the card's heading must go with it, or it names an empty box.
  const [todosGone, setTodosGone] = React.useState(false);
  const raisedByMe = contractorMode && ticket.requester_contractor_id
    && String(ticket.requester_contractor_id) === String(contractor?.id || '');
  const who = contractorMode
    ? (ticket.requester_id && ticket.requester_name
      ? [{ key: `u:${ticket.requester_id}`, name: ticket.requester_name, label: 'raised it' }]
      : ticket.requester_contractor_id && ticket.requester_name && !raisedByMe
        ? [{ key: `c:${ticket.requester_contractor_id}`, name: ticket.requester_name, label: 'raised it' }]
        : [])
    : [
      ticket.contractor_id && ticket.contractor_name
        && { key: `c:${ticket.contractor_id}`, name: ticket.contractor_name, label: 'contractor' },
      ticket.requester_id && ticket.requester_name
        && { key: `u:${ticket.requester_id}`, name: ticket.requester_name, label: 'raised it' },
      ticket.requester_contractor_id && ticket.requester_name
        && { key: `c:${ticket.requester_contractor_id}`, name: ticket.requester_name, label: 'raised it' }
    ].filter(Boolean);
  return (
    <div className="tk-docs">
      <TicketDocs ticketId={ticket.id} />
      {todosOn && !todosGone && (
        <section className="tk-card" aria-labelledby="tk-todos-h">
          <h2 className="tk-card-title" id="tk-todos-h">To-dos</h2>
          <TicketTodos ticketId={ticket.id} who={who} onGone={() => setTodosGone(true)}
                       {...(contractorMode ? { auth, todoPath: '/todo' } : {})} />
        </section>
      )}
    </div>
  );
}

/** FILES: what is on the ticket; adding is anyone's, removing an office job. */
export function FilesTab() {
  const { ticket, role, locked, contractorMode, loadSummary } = useOutletContext();
  return (
    <div className="tk-files">
      <TicketAttachments ticketId={ticket.id}
                         canUpload={!(contractorMode && locked)}
                         canRemove={!contractorMode && role === 'admin'}
                         onChanged={loadSummary} />
    </div>
  );
}

/** HISTORY: everything recorded against it, newest first. */
export function HistoryTab() {
  const { ticket } = useOutletContext();
  return (
    <div className="tk-history">
      <ActivityTrail ticketId={ticket.id} />
    </div>
  );
}
